import React, { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import ProductCard from "../Card/ProductCard";
import { useOutletContext } from "react-router-dom";
import { fetchLimitedStocks } from "../../../DataApi/miniStore/fetchLimitedStocks";
import { CartContext } from "../../../contexts/MiniStore/CartContext";
import { MINI_STORE_PAGINATION_LIMIT } from "../../../utilities/constants";
import { enqueueSnackbar } from "notistack";
import { MiniStoreThemeContext } from "../../../MiniStoreContexts/MiniStoreThemeContext";
import { PropagateLoader } from "react-spinners";
import ScrollToTop from "react-scroll-to-top";
import { FaArrowUp } from "react-icons/fa";
import { fetchPromotions } from "../../../DataApi/miniStore/fetchPromotions";
import { isEqual } from "lodash";

const MsAllPromotions = () => {
  const { isGrid, handleOpenProductModal } = useOutletContext();
  const [isLoading, setIsLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [endOfList, setEndOfList] = useState("");
  const endingDivRef = useRef();
  const { theme } = useContext(MiniStoreThemeContext);
  const { cart, addToCart, updateQuantity, products, setProducts } =
    useContext(CartContext);

  const getPromotions = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const promotionsList = await fetchPromotions({
        limit: MINI_STORE_PAGINATION_LIMIT,
        offset,
        query: "",
      });
      if (
        promotionsList?.status === 404 &&
        promotionsList?.message === "No promotional products found"
      ) {
        let value = products?.length > 0 ? "" : promotionsList?.message;
        setEndOfList(value);
      }
      if (promotionsList?.status === 200) {
        // console.log("promotionsList", promotionsList);

        const formattedPromotionsList = promotionsList.result.map(
          (product) => ({
            id: product?.id,
            title: product?.name || "",
            rating: product?.rating_avg || 0,
            quantity: 0,
            currency: product?.currency_id?.[1],
            price: Number(product?.lst_price.toFixed(2)) || 0,
            newPrice: Number(product?.pricelist_price.toFixed(2)) || 0,
            has_discounted_price: product?.has_discounted_price || false,
            discount: 0,
            description: product?.description_sale || "",
            image_1920: product?.img_url,
            hasVariant: product?.attribute_line_ids?.length > 0 ? true : false,
            availableQty: product?.qty_available || 0,
            variant_ids: product?.product_variant_ids || [],
          })
        );
        setProducts((prev) => [...prev, ...formattedPromotionsList]);
        setOffset(offset + MINI_STORE_PAGINATION_LIMIT);

        if (formattedPromotionsList.length < promotionsList?.count) {
          setHasMore(true);
        } else {
          setHasMore(true);
        }
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching promotions:", error);
    }
    setIsLoading(false);
  };
  const handleAddToCart = async (product) => {
    if (product?.availableQty < 1) {
      enqueueSnackbar("Product quantity not available", { variant: "warning" });
      return;
    }

    if (product?.hasVariant) {
      handleOpenProductModal(product);
    } else {
      addToCart({
        productId: product.variant_ids?.[0],
      });
    }
  };
  const fetchMoreData = () => {
    if (!isLoading && hasMore) {
      getPromotions();
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fetchMoreData();
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 1.0,
      }
    );

    const current = endingDivRef.current;
    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) observer.unobserve(current);
    };
  }, [hasMore, isLoading, offset]);
  useEffect(() => {
    return () => {
      const body = document.getElementsByTagName("body")?.[0];

      if (body) {
        body.scrollIntoView();
      }

      setOffset(0);
      setProducts([]);
    };
  }, []);

  return (
    <div>
      <CardsWrapper isGrid={isGrid}>
        {Array.isArray(products) &&
          products.length > 0 &&
          products.map((product) => {
            const matchingCartItems = cart.filter((item) =>
              product.variant_ids?.includes(item.id)
            );
            const quantity = matchingCartItems.reduce(
              (sum, item) => sum + (item.quantity || 0),
              0
            );
            return (
              <ProductCard
                key={product.id}
                id={product.id}
                title={product.title}
                rating={product.rating}
                quantity={quantity || 0}
                currency={product.currency}
                price={product.price}
                newPrice={product.newPrice}
                has_discounted_price={product.has_discounted_price}
                img={product.image_1920}
                onClick={() => handleOpenProductModal(product)}
                onQuantityChange={(id, change) =>
                  updateQuantity({
                    productId: product.variant_ids?.[0],
                    qtyChange: change,
                  })
                }
                onAddToCart={() => handleAddToCart(product)}
                viewType={isGrid ? "grid" : "list"}
                hasVariant={product.hasVariant}
              />
            );
          })}
      </CardsWrapper>
      <ScrollToTop
        smooth
        color={theme.textColor.primary}
        component={
          <FaArrowUp
            style={{
              width: "28px",
              height: "28px",
              color: theme.textColor.primary,
            }}
          />
        }
        style={{ backgroundColor: theme.primaryColor.primary }}
      />
      <div
        ref={endingDivRef}
        style={{
          width: "100%",
          textAlign: "center",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {isLoading ? (
          <PropagateLoader color={theme.primaryColor.primary} />
        ) : (
          endOfList
        )}
      </div>
    </div>
  );
};

export default MsAllPromotions;

const CardsWrapper = styled.div`
  display: grid;
  grid-template-columns: ${({ isGrid }) =>
    isGrid ? "repeat(auto-fit, minmax(250px, 1fr))" : "1fr"};
  gap: 16px;
  width: 100%;
  max-width: 900px;
  place-items: start center;
  @media (max-width: 768px) {
    grid-template-columns: ${({ isGrid }) =>
      isGrid ? "repeat(2, minmax(0, 1fr))" : "1fr"};
  }
`;
